import React, { useMemo } from 'react'

import { useGetPlatforms } from '../../../../api/getPlatforms'
import usePlatformsModalsStore from '../../../../stores/platformsModals.store'
import { PLATFORMS_NAMES } from '../../../../../../utils/platforms'

import { ApproveAccess } from './ApproveAccess/ApproveAccess'
import { ConnectAccount } from './ConnectAccount/ConnectAccount'
import { NewSelectAccountIds } from './SelectAccountIds/NewSelectAccountIds'

interface ConnectModalAccountContentProps {
  currentStep: number
  editAccount?: number
  setStep: (s: number) => void
  onBack: () => void
  onClose?: () => void
  isEdit?: boolean
}

export const GoogleConnectModalContent = ({
  currentStep,
  editAccount,
  setStep,
  onBack,
  onClose,
  isEdit,
}: ConnectModalAccountContentProps) => {
  const { data } = useGetPlatforms({})
  const { closeModal, setSelectedConnectionId } = usePlatformsModalsStore()

  const handleClose = () => {
    closeModal()
    onClose && onClose()
  }

  const connection = useMemo(() => {
    // get's connectionId from getPlatforms query if user already connected or from connectionForm
    // insures that even if user refreshes page, user can finish wizard or edit connection
    if (!data) return undefined

    const account = data.find((platform) => {
      if (editAccount) {
        return platform.connectionId === editAccount
      } else {
        return (
          platform.platformType === PLATFORMS_NAMES.google &&
          platform.connectionStatus === 'Pending'
        )
      }
    })

    return {
      id: account?.connectionId || 0,
      status: account?.connectionStatus || '',
      connectionType: account?.connectionType || undefined,
    }
  }, [data, editAccount])

  const startOver = () => {
    setStep(1)
  }

  const setPlatformId = (id: number, isWithAccess?: boolean) =>
    isWithAccess ? setApproveAccess() : setStep(2)

  const setApproveAccess = () => {
    setSelectedConnectionId(connection?.id)
    setStep(3)
  }

  if (connection) {
    // if user have connectionId, he can't connect account again, so we render other components
    switch (currentStep) {
      case 2:
        return (
          <ApproveAccess
            connectionId={connection.id}
            onSubmit={setApproveAccess}
            onBack={startOver}
          />
        )
      case 3: {
        const props = {
          connectionId: connection.id,
          onClose: handleClose,
          isEdit,
          onBack,
        }
        return <NewSelectAccountIds {...props} />
      }
      default:
        return <ConnectAccount submitPlatformId={setPlatformId} onClose={handleClose} />
    }
  } else {
    // if user don't have connectionId, we render only ConnectAccount component
    return <ConnectAccount submitPlatformId={setPlatformId} onClose={handleClose} />
  }
}
